"use client";

import type { MusicListTrack } from "./BlockMusicList";

import { clsx } from "clsx";
import dynamic from "next/dynamic";
import { cancelFrame, frame } from "motion";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useMotionValue, type MotionValue } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useSpinDelay } from "spin-delay";

import { MediaRow, MediaListPagination, useMediaListPagination } from "@ui/components/core/MediaList";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { Link } from "@ui/components/core/Link";
import { AuthLink } from "@ui/components/core/AuthDialog/AuthLink";
import type { OrbTextureName } from "@ui/components/core/Orb/Orb";
import { SessionStorageKeys, setSessionStorage } from "@utilities/sessionStorage";
import { type CreateAudioReturn, createAudio } from "../BlockInteractCreateOmnibox/createAudio";
import type { ComponentProps } from "react";

// Path to the music app, relative to NEXT_PUBLIC_WEB_APP_URL (AuthLink prepends it).
const MUSIC_APP_PATH = "/music";

// Hands the track's description to the music app's omnibox so the "Edit Song"
// CTA lands on /music with the prompt pre-filled. The app reads + clears the
// key on mount; same-origin same-tab navigation keeps sessionStorage intact.
function seedMusicOmnibox(prompt: string | null) {
  if (!prompt) return;
  setSessionStorage(SessionStorageKeys.OMNIBOX_MUSIC_PROMPT, prompt);
}

function TrackCta({
  track,
  editSongLabel,
  buttonVariants,
  className,
  stopRowPropagation = false,
}: {
  track: MusicListTrack;
  editSongLabel: string;
  buttonVariants: ComponentProps<typeof Link>["buttonVariants"];
  className?: string;
  stopRowPropagation?: boolean;
}) {
  // Editor-set custom CTA: plain link, no auth gating or omnibox seeding — the
  // editor pointed it somewhere intentional that may not be the music app.
  if (track.ctaLink && isValidLinkWithLabel(track.ctaLink)) {
    return (
      <Link
        {...track.ctaLink}
        asButton
        buttonVariants={buttonVariants}
        className={className}
        onClick={stopRowPropagation ? e => e.stopPropagation() : undefined}
      >
        {track.ctaLink.label}
      </Link>
    );
  }

  // Default "Edit Song": seed the omnibox, then AuthLink either sends a signed-in
  // user straight to /music or opens the AuthDialog and redirects there post-auth.
  const authLink = (
    <AuthLink
      href={MUSIC_APP_PATH}
      buttonVariants={buttonVariants}
      className={className}
      onClick={() => seedMusicOmnibox(track.detailedDescription ?? track.description)}
    >
      {editSongLabel}
    </AuthLink>
  );

  // AuthLink's onClick has no event arg, so stop row selection via a wrapper.
  // `tw-contents` keeps the wrapper out of layout so the button stays a flex
  // child of the row (preserving its absolute centering) while still catching
  // the bubbling click.
  return stopRowPropagation ? (
    <span className='tw-contents' onClick={e => e.stopPropagation()}>
      {authLink}
    </span>
  ) : (
    authLink
  );
}

const Orb = dynamic(() => import("@ui/components/core/Orb/Orb").then(m => m.Orb), {
  ssr: false,
});

const CREATIVE_TEXTURES: OrbTextureName[] = [
  "creative-1",
  "creative-2",
  "creative-3",
  "creative-4",
  "creative-5",
  "creative-6",
  "creative-7",
  "creative-8",
  "creative-9",
];

const TRACKS_PER_PAGE = 9;

function getTrackTexture(index: number): OrbTextureName {
  return CREATIVE_TEXTURES[index % CREATIVE_TEXTURES.length];
}

function TrackRow({
  track,
  globalIndex,
  isSelected,
  isPlaying,
  analyser,
  onSelect,
  editSongLabel,
}: {
  track: MusicListTrack;
  globalIndex: number;
  isSelected: boolean;
  isPlaying: boolean;
  analyser: AnalyserNode | null;
  onSelect: () => void;
  editSongLabel: string;
}) {
  const isTrackPlaying = isSelected && isPlaying;

  return (
    <MediaRow isSelected={isSelected} onSelect={onSelect} className='tw-relative'>
      <div
        className={clsx(
          "tw-relative tw-size-9 tw-shrink-0 tw-overflow-hidden tw-rounded-lg tw-transition-transform tw-duration-200 tw-ease-out",
          isTrackPlaying && "tw-scale-110",
        )}
      >
        <Orb
          texture={getTrackTexture(globalIndex)}
          animated={isTrackPlaying}
          outputAnalyser={isSelected ? analyser : undefined}
          cornerRadius={8}
          image={{ sizes: "36px" }}
        />
        <div aria-hidden='true' className='tw-absolute tw-inset-0 tw-bg-black/10' />
        <div
          className={clsx(
            "tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-duration-150",
            isTrackPlaying
              ? "tw-opacity-100"
              : "tw-opacity-0 group-hover/media-row:tw-opacity-100 group-focus-within/media-row:tw-opacity-100",
          )}
        >
          <PlayPauseButton
            isPlaying={isTrackPlaying}
            variant='ghost'
            iconSize='sm'
            className='tw-text-white tw-drop-shadow'
          />
        </div>
      </div>

      <p className='tw-type-sm tw-min-w-0 tw-truncate tw-text-black tw-py-1'>{track.title}</p>

      {track.description && (
        <p
          className={clsx(
            "tw-type-sm tw-pointer-events-none tw-min-w-0 tw-flex-1 tw-truncate tw-text-right tw-text-gray-500 tw-transition-opacity tw-duration-200 tw-ease-out tw-py-1",
            isSelected && "lg:tw-opacity-100 tw-opacity-0",
          )}
        >
          {track.description}
        </p>
      )}

      <TrackCta
        track={track}
        editSongLabel={editSongLabel}
        buttonVariants={{ variant: "elevated" }}
        className={clsx(
          "tw-absolute tw-right-3 tw-z-10 tw-h-9 tw-shrink-0 tw-transition-opacity tw-duration-200 tw-ease-out lg:tw-hidden",
          isSelected ? "tw-pointer-events-auto tw-opacity-100" : "tw-pointer-events-none tw-opacity-0",
        )}
        stopRowPropagation
      />
    </MediaRow>
  );
}

function TrackDetailPanel({
  track,
  selectedIndex,
  isPlaying,
  showLoading,
  analyser,
  onTogglePlay,
  onSeek,
  progressBarTransform,
  playLabel,
  pauseLabel,
  editSongLabel,
}: {
  track: MusicListTrack;
  selectedIndex: number;
  isPlaying: boolean;
  showLoading: boolean;
  analyser: AnalyserNode | null;
  onTogglePlay: () => void;
  onSeek: (fraction: number) => void;
  progressBarTransform: MotionValue<string>;
  playLabel: string;
  pauseLabel: string;
  editSongLabel: string;
}) {
  return (
    <div className='tw-col-span-1 tw-hidden tw-flex-col tw-gap-5 lg:tw-flex'>
      <button
        type='button'
        className='tw-relative tw-aspect-square tw-w-full tw-overflow-hidden tw-rounded-3xl tw-bg-gray-200 tw-outline-none focus-visible:tw-outline-focus'
        onClick={onTogglePlay}
        aria-label={isPlaying ? pauseLabel : playLabel}
      >
        <AnimatePresence initial={false} mode='popLayout'>
          <m.div
            key={selectedIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className='tw-absolute tw-inset-0'
          >
            <Orb
              texture={getTrackTexture(selectedIndex)}
              outputAnalyser={analyser}
              cornerRadius={24}
              animated={isPlaying || showLoading}
            />
          </m.div>
        </AnimatePresence>
        <div
          aria-hidden='true'
          className='tw-absolute tw-inset-0 tw-rounded-3xl tw-ring-0.5 tw-ring-inset tw-ring-black/7.5'
        />

        <div className='tw-absolute tw-inset-x-4 tw-bottom-4 tw-flex tw-items-center tw-gap-3'>
          <div className='tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-sm'>
            <PlayPauseButton isPlaying={isPlaying} variant='ghost' iconSize='sm' className='tw-text-black' />
          </div>

          {}
          <div
            className='tw-flex-1 tw-cursor-pointer tw-overflow-hidden tw-rounded-full tw-bg-white/20'
            onClick={e => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              onSeek((e.clientX - rect.left) / rect.width);
            }}
          >
            <m.div
              className='tw-pointer-events-none tw-h-1 tw-origin-left tw-rounded-full tw-bg-white'
              style={{ transform: progressBarTransform }}
            />
          </div>
        </div>
      </button>

      <div className='tw-flex tw-flex-col tw-px-4'>
        <div className='tw-flex tw-flex-col tw-mb-7'>
          <p className='tw-type-xl tw-text-black tw-mb-3'>{track.title}</p>
          <p className='tw-type-xs tw-line-clamp-3 tw-min-h-[3lh] tw-pt-1 tw-text-gray-500'>
            {track.detailedDescription ?? track.description}
          </p>
        </div>

        <TrackCta
          track={track}
          editSongLabel={editSongLabel}
          buttonVariants={{ variant: "elevated", size: "md" }}
          className='tw-w-full tw-justify-center'
        />
      </div>
    </div>
  );
}

export function BlockMusicListClient({ tracks, editSongLabel }: { tracks: MusicListTrack[]; editSongLabel: string }) {
  const t = useTranslations("ClientKeys.Globals");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [playState, setPlayState] = useState<"idle" | "loading" | "playing" | "paused">("idle");
  const showLoading = useSpinDelay(playState === "loading", { delay: 200, minDuration: 500 });

  const audioRef = useRef<CreateAudioReturn | null>(null);
  const progressBarTransform = useMotionValue("scaleX(0)");

  const selectedTrack = tracks[selectedIndex];

  const { currentPage, totalPages, visibleItems, goNext, goPrevious } = useMediaListPagination({
    items: tracks,
    perPage: TRACKS_PER_PAGE,
  });

  const cleanup = useCallback(() => {
    audioRef.current?.cleanup();
    audioRef.current = null;
    setAnalyser(null);
    setPlayState("idle");
    progressBarTransform.set("scaleX(0)");
  }, []);

  const play = useCallback(
    (track: MusicListTrack) => {
      cleanup();

      const audio = createAudio({
        src: track.audioUrl,
        fade: false,
        onLoaded: ({ analyser }) => {
          setAnalyser(analyser);
          audioRef.current?.play();
        },
        onPlayStarted: () => {
          setPlayState("playing");
        },
        onEnded: () => {
          setPlayState("idle");
          progressBarTransform.set("scaleX(0)");
        },
        onError: () => {
          setPlayState("idle");
          progressBarTransform.set("scaleX(0)");
        },
      });

      audioRef.current = audio;
      setPlayState("loading");
      audio.load();
    },
    [cleanup, progressBarTransform],
  );

  const handleTogglePlay = useCallback(() => {
    if (!selectedTrack) return;

    if (playState === "playing") {
      audioRef.current?.pause();
      setPlayState("paused");
      return;
    }

    if (playState === "loading") {
      cleanup();
      return;
    }

    if (playState === "paused" && audioRef.current) {
      setPlayState("playing");
      audioRef.current.play();
      return;
    }

    play(selectedTrack);
  }, [playState, selectedTrack, play, cleanup]);

  const handleSelectTrack = useCallback(
    (index: number) => {
      const wasActive = playState === "playing" || playState === "loading" || playState === "paused";
      const isSameTrack = index === selectedIndex;

      setSelectedIndex(index);

      if (isSameTrack) {
        handleTogglePlay();
        return;
      }

      if (wasActive) {
        play(tracks[index]);
      } else {
        cleanup();
      }
    },
    [selectedIndex, playState, tracks, play, cleanup, handleTogglePlay],
  );

  const handleSeek = useCallback(
    (fraction: number) => {
      const audio = audioRef.current?.getAudio();
      if (!audio || !audio.duration) return;
      const clamped = Math.max(0, Math.min(1, fraction));
      audio.currentTime = clamped * audio.duration;
      progressBarTransform.set(`scaleX(${clamped})`);
    },
    [progressBarTransform],
  );

  useEffect(() => {
    if (playState !== "playing") return;

    function updateProgress() {
      const audio = audioRef.current?.getAudio();
      if (!audio) return;
      const progress = audio.currentTime / audio.duration;
      progressBarTransform.set(`scaleX(${progress})`);
    }

    frame.render(updateProgress, true);
    return () => cancelFrame(updateProgress);
  }, [playState, progressBarTransform]);

  useEffect(() => cleanup, [cleanup]);

  const isPlaying = playState === "playing";

  return (
    <div className='tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-3'>
      {/* Left panel — track list */}
      <div className='tw-col-span-1 tw-flex tw-flex-col tw-gap-5 tw-px-4 lg:tw-col-span-2 lg:tw-px-8'>
        <div className='tw-relative tw-flex-1' style={{ minHeight: TRACKS_PER_PAGE * 52 + (TRACKS_PER_PAGE - 1) * 2 }}>
          <AnimatePresence initial={false} mode='wait'>
            <m.div
              key={currentPage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className='tw-flex tw-flex-col tw-gap-0.5'
            >
              {visibleItems.map((track, index) => {
                const globalIndex = currentPage * TRACKS_PER_PAGE + index;
                return (
                  <TrackRow
                    key={track.id}
                    track={track}
                    globalIndex={globalIndex}
                    isSelected={globalIndex === selectedIndex}
                    isPlaying={isPlaying}
                    analyser={analyser}
                    onSelect={() => handleSelectTrack(globalIndex)}
                    editSongLabel={editSongLabel}
                  />
                );
              })}
            </m.div>
          </AnimatePresence>
        </div>

        {totalPages > 1 && (
          <MediaListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onNext={goNext}
            onPrevious={goPrevious}
            previousLabel={t("previous")}
            nextLabel={t("next")}
          />
        )}
      </div>

      {selectedTrack && (
        <TrackDetailPanel
          track={selectedTrack}
          selectedIndex={selectedIndex}
          isPlaying={isPlaying}
          showLoading={showLoading}
          analyser={analyser}
          onTogglePlay={handleTogglePlay}
          onSeek={handleSeek}
          progressBarTransform={progressBarTransform}
          playLabel={t("play")}
          pauseLabel={t("pause")}
          editSongLabel={editSongLabel}
        />
      )}
    </div>
  );
}
